import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Star, GripVertical } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { SalesFunnel } from "@/types/lead";
import { resolveFunnelDisplayName } from "@/lib/funnel-display-label";
import type { WorkspaceIntegrationsMap } from "@/hooks/useWorkspaceIntegrationsMap";
import {
  canDeleteFunnel,
  getFunnelDeletionBlockReason,
  getFunnelDeletionWarning,
} from "@/lib/funnel-deletion-protection";
import { DndContext, type DragEndEvent, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface FunnelManagerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  funnels: SalesFunnel[];
  onCreateFunnel: (data: { name: string; description?: string; color?: string }) => Promise<any>;
  onUpdateFunnel: (id: string, data: Partial<SalesFunnel>) => Promise<boolean>;
  onDeleteFunnel: (id: string) => Promise<boolean>;
  onSetDefault: (id: string) => Promise<boolean>;
  onReorderFunnels?: (orderedIds: string[]) => Promise<boolean>;
  /** Quando false, oculta criação/edição/exclusão (somente leitura). */
  canManage?: boolean;
  integrationById?: WorkspaceIntegrationsMap;
}

const colorOptions = [
  { value: "bg-blue-500", label: "Azul" },
  { value: "bg-green-500", label: "Verde" },
  { value: "bg-yellow-500", label: "Amarelo" },
  { value: "bg-orange-500", label: "Laranja" },
  { value: "bg-red-500", label: "Vermelho" },
  { value: "bg-purple-500", label: "Roxo" },
  { value: "bg-pink-500", label: "Rosa" },
  { value: "bg-indigo-500", label: "Índigo" },
  { value: "bg-teal-500", label: "Turquesa" },
  { value: "bg-cyan-500", label: "Ciano" },
];

function SortableFunnelItem({
  funnel,
  funnelCount,
  canManage,
  integrationById,
  onSetDefault,
  onEdit,
  onDeleteClick,
  sortable,
}: {
  funnel: SalesFunnel;
  funnelCount: number;
  canManage: boolean;
  integrationById?: WorkspaceIntegrationsMap;
  onSetDefault: (id: string) => void;
  onEdit: (funnel: SalesFunnel) => void;
  onDeleteClick: (id: string) => void;
  sortable: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: funnel.id,
    disabled: !sortable,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-3 border rounded-lg bg-card hover:bg-muted/50 transition-colors"
    >
      {sortable ? (
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing touch-none">
          <GripVertical className="w-4 h-4 text-muted-foreground" />
        </div>
      ) : (
        <GripVertical className="w-4 h-4 text-muted-foreground/40" />
      )}
      <div className={cn("w-4 h-4 rounded-full shrink-0", funnel.color)} />
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{resolveFunnelDisplayName(funnel, integrationById)}</p>
        {funnel.description && (
          <p className="text-xs text-muted-foreground truncate">{funnel.description}</p>
        )}
      </div>
      {funnel.is_default && (
        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500 shrink-0" />
      )}
      <div className="flex items-center gap-1 shrink-0">
        {canManage && !funnel.is_default && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => onSetDefault(funnel.id)}
            title="Definir como padrão"
          >
            <Star className="w-4 h-4" />
          </Button>
        )}
        {canManage && (
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(funnel)}>
            <Pencil className="w-4 h-4" />
          </Button>
        )}
        {canManage && (() => {
          const deletionCtx = { funnelCount, integrationById };
          const blockReason = getFunnelDeletionBlockReason(funnel, deletionCtx);
          const deletable = canDeleteFunnel(funnel, deletionCtx);
          return (
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "h-8 w-8",
                deletable
                  ? "text-destructive hover:text-destructive"
                  : "text-muted-foreground/40 cursor-not-allowed"
              )}
              onClick={() => deletable && onDeleteClick(funnel.id)}
              disabled={!deletable}
              title={blockReason ?? "Excluir funil"}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          );
        })()}
      </div>
    </div>
  );
}

export function FunnelManagerDialog({
  open,
  onOpenChange,
  funnels,
  onCreateFunnel,
  onUpdateFunnel,
  onDeleteFunnel,
  onSetDefault,
  onReorderFunnels,
  canManage = true,
  integrationById,
}: FunnelManagerDialogProps) {
  const [orderedFunnels, setOrderedFunnels] = useState<SalesFunnel[]>(funnels);
  const [isCreating, setIsCreating] = useState(false);
  const [editingFunnel, setEditingFunnel] = useState<SalesFunnel | null>(null);
  const [formData, setFormData] = useState({ name: "", description: "", color: "bg-blue-500" });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [funnelToDelete, setFunnelToDelete] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isReordering, setIsReordering] = useState(false);

  const canReorder = Boolean(canManage && onReorderFunnels && orderedFunnels.length > 1);

  useEffect(() => {
    setOrderedFunnels(funnels);
  }, [funnels]);

  const resetForm = () => {
    setFormData({ name: "", description: "", color: "bg-blue-500" });
    setIsCreating(false);
    setEditingFunnel(null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    if (!onReorderFunnels) return;

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = orderedFunnels.findIndex((f) => f.id === active.id);
    const newIndex = orderedFunnels.findIndex((f) => f.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const previous = orderedFunnels;
    const next = arrayMove(orderedFunnels, oldIndex, newIndex);
    setOrderedFunnels(next);
    setIsReordering(true);

    try {
      const ok = await onReorderFunnels(next.map((f) => f.id));
      if (!ok) setOrderedFunnels(previous);
    } finally {
      setIsReordering(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) return;

    setIsSaving(true);
    try {
      if (editingFunnel) {
        await onUpdateFunnel(editingFunnel.id, {
          name: formData.name,
          description: formData.description || undefined,
          color: formData.color,
        });
      } else {
        await onCreateFunnel({
          name: formData.name,
          description: formData.description || undefined,
          color: formData.color,
        });
      }
      resetForm();
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (funnel: SalesFunnel) => {
    setEditingFunnel(funnel);
    setFormData({
      name: funnel.name,
      description: funnel.description || "",
      color: funnel.color,
    });
    setIsCreating(true);
  };

  const handleDeleteClick = (id: string) => {
    const funnel = orderedFunnels.find((f) => f.id === id);
    if (!funnel) return;

    const blockReason = getFunnelDeletionBlockReason(funnel, {
      funnelCount: orderedFunnels.length,
      integrationById,
    });
    if (blockReason) return;

    setFunnelToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!funnelToDelete) return;
    await onDeleteFunnel(funnelToDelete);
    setDeleteConfirmOpen(false);
    setFunnelToDelete(null);
  };

  const funnelPendingDelete = funnelToDelete
    ? orderedFunnels.find((f) => f.id === funnelToDelete) ?? null
    : null;
  const pendingDeleteWarning = funnelPendingDelete
    ? getFunnelDeletionWarning(funnelPendingDelete, {
        funnelCount: orderedFunnels.length,
        integrationById,
      })
    : null;

  const funnelList = (
    <div className="space-y-2">
      {orderedFunnels.map((funnel) => (
        <SortableFunnelItem
          key={funnel.id}
          funnel={funnel}
          funnelCount={orderedFunnels.length}
          canManage={canManage}
          integrationById={integrationById}
          onSetDefault={onSetDefault}
          onEdit={handleEdit}
          onDeleteClick={handleDeleteClick}
          sortable={canReorder}
        />
      ))}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] sm:max-w-2xl max-h-[calc(100dvh-2rem)] flex flex-col gap-4 overflow-hidden p-4 sm:p-6">
        <DialogHeader className="shrink-0 pr-8">
          <DialogTitle>Gerenciar Funis de Vendas</DialogTitle>
          <DialogDescription>
            {canReorder
              ? "Crie e organize seus funis. Arraste para reordenar."
              : "Crie e organize seus funis para diferentes processos de vendas."}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain pr-1">
          {canReorder ? (
            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext
                items={orderedFunnels.map((f) => f.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className={cn(isReordering && "pointer-events-none opacity-70")}>{funnelList}</div>
              </SortableContext>
            </DndContext>
          ) : (
            funnelList
          )}

          {canManage && isCreating ? (
            <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
              <div className="space-y-2">
                <Label htmlFor="funnel-name">Nome do Funil *</Label>
                <Input
                  id="funnel-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Vendas B2B"
                  autoFocus
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="funnel-description">Descrição</Label>
                <Textarea
                  id="funnel-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descreva o propósito deste funil..."
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="funnel-color">Cor</Label>
                <Select
                  value={formData.color}
                  onValueChange={(value) => setFormData({ ...formData, color: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {colorOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center gap-2">
                          <div className={cn("w-4 h-4 rounded-full", option.value)} />
                          {option.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2">
                <Button onClick={handleSave} disabled={!formData.name.trim() || isSaving} className="flex-1">
                  {isSaving ? "Salvando..." : editingFunnel ? "Atualizar" : "Criar"}
                </Button>
                <Button onClick={resetForm} variant="outline" className="flex-1">
                  Cancelar
                </Button>
              </div>
            </div>
          ) : canManage ? (
            <Button onClick={() => setIsCreating(true)} variant="outline" className="w-full">
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Funil
            </Button>
          ) : null}
        </div>
      </DialogContent>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  Tem certeza que deseja excluir este funil? Os estágios serão removidos e os leads
                  associados podem ficar sem funil. Esta ação não pode ser desfeita.
                </p>
                {pendingDeleteWarning && (
                  <p className="text-amber-600 dark:text-amber-500">{pendingDeleteWarning}</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
